import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Users, Plus, Trash2, X, Edit2, AlertCircle, Phone, MapPin, User } from 'lucide-react';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  gender: string;
  country: string;
  created_at?: string;
}

interface EditableCustomer extends Customer {
  isEditing?: boolean;
}

export const CustomersPage: React.FC = () => {
  const { t, dir } = useLanguage();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);

  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    gender: '',
    country: 'LY'
  });

  const [editingCustomer, setEditingCustomer] = useState<EditableCustomer | null>(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/customers');
      const data = await response.json();
      if (data.success) {
        setCustomers(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCustomer)
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(prev => [data.data, ...prev]);
        setNewCustomer({
          first_name: '',
          last_name: '',
          phone: '',
          gender: '',
          country: 'LY'
        });
        setShowAddForm(false);
        setError(null);
      }
    } catch (err) {
      setError(t.failedToAddCustomer);
    }
  };

  const handleDeleteCustomer = async (id: number) => {
    try {
      const response = await fetch(`/api/customers?id=${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setCustomers(prev => prev.filter(c => c.id !== id));
        setShowDeleteConfirm(null);
        setError(null);
      }
    } catch (err) {
      setError(t.failedToDeleteCustomer);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    try {
      const response = await fetch(`/api/customers?id=${editingCustomer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: editingCustomer.first_name,
          last_name: editingCustomer.last_name,
          phone: editingCustomer.phone,
          gender: editingCustomer.gender,
          country: editingCustomer.country
        })
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(prev => prev.map(c => c.id === editingCustomer.id ? data.data : c));
        setEditingCustomer(null);
        setError(null);
      }
    } catch (err) {
      setError(t.failedToUpdateCustomer);
    }
  };

  const startEditing = (customer: Customer) => {
    setEditingCustomer({ ...customer, isEditing: true });
  };

  return (
    <div className="bg-neo-white border-4 border-neo-black shadow-neo overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="mb-6 pb-6 border-b-4 border-neo-black flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-neo-black font-mono uppercase">{t.customersPageTitle}</h3>
            <p className="text-neo-black mt-1 font-mono text-sm">{t.customersPageDesc}</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-neo-green border-4 border-neo-black px-4 py-2 text-sm font-bold font-mono shadow-neo-sm flex items-center gap-2 hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
          >
            <Plus className="w-4 h-4" />
            {t.addCustomer}
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-neo-red border-4 border-neo-black text-neo-black px-4 py-3 shadow-neo-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-mono font-bold">{error}</span>
          </div>
        )}

        {showAddForm && (
          <form onSubmit={handleAddCustomer} className="bg-neo-yellow border-4 border-neo-black p-6 shadow-neo mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold font-mono uppercase">{t.newCustomer}</h4>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder={t.firstName}
                value={newCustomer.first_name}
                onChange={(e) => setNewCustomer({...newCustomer, first_name: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <input
                type="text"
                placeholder={t.lastName}
                value={newCustomer.last_name}
                onChange={(e) => setNewCustomer({...newCustomer, last_name: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <input
                type="tel"
                placeholder={t.phoneNumber}
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <select
                value={newCustomer.gender}
                onChange={(e) => setNewCustomer({...newCustomer, gender: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              >
                <option value="">{t.gender}</option>
                <option value="m">{t.male}</option>
                <option value="f">{t.female}</option>
              </select>
              <input
                type="text"
                placeholder={t.country}
                value={newCustomer.country}
                onChange={(e) => setNewCustomer({...newCustomer, country: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm md:col-span-2"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full mt-4 bg-neo-blue border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
            >
              {t.createCustomer}
            </button>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-neo-black font-mono">Loading...</div>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neo-black">
            <Users className="w-16 h-16 mb-4" />
            <p className="text-lg font-bold font-mono">{t.noCustomers}</p>
            <p className="text-sm mt-2 font-mono">{t.noCustomersDesc}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {customers.map((customer) => (
              <div key={customer.id} className="bg-neo-white border-4 border-neo-black p-4 shadow-neo-sm">
                {editingCustomer?.id === customer.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-neo-black font-mono">{t.editCustomer}</span>
                      <button
                        onClick={() => setEditingCustomer(null)}
                        className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={editingCustomer.first_name}
                        onChange={(e) => setEditingCustomer({...editingCustomer, first_name: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <input
                        type="text"
                        value={editingCustomer.last_name}
                        onChange={(e) => setEditingCustomer({...editingCustomer, last_name: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <input
                        type="tel"
                        value={editingCustomer.phone}
                        onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <select
                        value={editingCustomer.gender}
                        onChange={(e) => setEditingCustomer({...editingCustomer, gender: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      >
                        <option value="m">{t.male}</option>
                        <option value="f">{t.female}</option>
                      </select>
                      <input
                        type="text"
                        value={editingCustomer.country}
                        onChange={(e) => setEditingCustomer({...editingCustomer, country: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm md:col-span-2"
                      />
                    </div>
                    <button
                      onClick={handleUpdateCustomer}
                      className="w-full bg-neo-green border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                    >
                      {t.save}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <User className="w-5 h-5 text-neo-black" />
                          <h4 className="text-lg font-bold text-neo-black font-mono">
                            {customer.first_name} {customer.last_name}
                          </h4>
                          <span className="text-xs bg-neo-blue border-4 border-neo-black px-2 py-0.5 shadow-neo-sm font-mono">
                            {customer.gender === 'm' ? t.male : t.female}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-neo-black" />
                            <span className="font-mono">{t.phoneNumber}:</span>
                            <span className="font-bold font-mono">{customer.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-neo-black" />
                            <span className="font-mono">{t.country}:</span>
                            <span className="font-bold font-mono">{customer.country}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEditing(customer)}
                          className="bg-neo-blue border-4 border-neo-black p-2 shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                          title={t.editCustomer}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(customer.id)}
                          className="bg-neo-red border-4 border-neo-black p-2 shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                          title={t.deleteCustomer}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {showDeleteConfirm === customer.id && (
                  <div className="mt-4 bg-neo-red border-4 border-neo-black p-4 shadow-neo-sm">
                    <p className="font-mono text-sm mb-3">{t.confirmDeleteCustomer}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDeleteCustomer(customer.id)}
                        className="flex-1 bg-neo-black border-4 border-neo-black py-2 text-sm font-bold font-mono text-neo-white shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                      >
                        {t.deleteCustomer}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="flex-1 bg-neo-white border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
